import React, { useEffect, useMemo, useState } from "react";
import {
  RefreshCw, Search, Link as LinkIcon, Trash2, ShieldAlert, ShieldCheck,
  ChevronRight, Loader2, AlertCircle, CheckCircle2,
} from "lucide-react";
import {
  adminStackUserAudit, adminStackUserLinkAsSupport, adminStackUserDelete,
  adminListHosting,
} from "../../lib/api";

const Section = ({ title, count, tone, children, testid }) => (
  <div className="border border-[#11151C]/10 rounded-2xl bg-white" data-testid={testid}>
    <div className="px-4 py-3 border-b border-[#11151C]/10 flex items-center justify-between">
      <h3 className="font-bold text-[#11151C]">{title}</h3>
      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
        tone === "ok" ? "bg-green-100 text-green-700" :
        tone === "warn" ? "bg-yellow-100 text-yellow-700" :
        "bg-red-100 text-red-700"
      }`}>{count}</span>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

export default function StackUserAuditPanel({ onToast }) {
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [hostings, setHostings] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState({}); // ref -> bool
  const [selected, setSelected] = useState({}); // ref -> bool (for orphan bulk delete)
  const [linkPick, setLinkPick] = useState(null); // { ref, suggested_id }

  const load = async () => {
    setLoading(true);
    try {
      const [a, h] = await Promise.all([adminStackUserAudit(), adminListHosting()]);
      setAudit(a);
      setHostings(h || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Audit failed", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Suggest hosting auto-match by stack user's contact email or name.
  const suggestHosting = (su) => {
    if (!hostings.length) return null;
    const email = (su.contact_email || "").toLowerCase().trim();
    const name = (su.name || "").toLowerCase().trim();
    if (email) {
      const m = hostings.find(
        (h) =>
          (h.customer_email || "").toLowerCase() === email ||
          (h.support_email || "").toLowerCase() === email,
      );
      if (m) return m;
    }
    if (name) {
      const m = hostings.find((h) => (h.domain_name || "").toLowerCase() === name);
      if (m) return m;
    }
    return null;
  };

  const filteredUnlinked = useMemo(() => {
    if (!audit?.unlinked) return [];
    const q = search.toLowerCase().trim();
    if (!q) return audit.unlinked;
    return audit.unlinked.filter(
      (u) =>
        (u.name || "").toLowerCase().includes(q) ||
        (u.contact_email || "").toLowerCase().includes(q) ||
        (u.id || "").toString().includes(q),
    );
  }, [audit, search]);

  const linkSupport = async (ref, hostingId, email) => {
    setBusy((b) => ({ ...b, [ref]: true }));
    try {
      await adminStackUserLinkAsSupport({ hosting_id: hostingId, stack_user_ref: ref, email });
      onToast?.("Linked as Support user");
      setLinkPick(null);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Link failed", "error");
    } finally {
      setBusy((b) => ({ ...b, [ref]: false }));
    }
  };

  const deleteOne = async (ref) => {
    if (!window.confirm(`Delete ${ref} on 20i?\nThis cannot be undone.`)) return;
    setBusy((b) => ({ ...b, [ref]: true }));
    try {
      const r = await adminStackUserDelete(ref);
      if (r.ok) onToast?.(`Deleted ${ref}`);
      else onToast?.("Delete returned non-OK from 20i", "error");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Delete failed", "error");
    } finally {
      setBusy((b) => ({ ...b, [ref]: false }));
    }
  };

  const autoLinkAll = async () => {
    const toLink = (audit?.unlinked || [])
      .map((u) => ({ u, h: suggestHosting(u) }))
      .filter((x) => x.h);
    if (!toLink.length) {
      onToast?.("No high-confidence matches found", "error");
      return;
    }
    if (!window.confirm(`Auto-link ${toLink.length} stack user(s) as Support user on their matched hosting?`)) return;
    let ok = 0;
    let fail = 0;
    for (const { u, h } of toLink) {
      try {
        await adminStackUserLinkAsSupport({
          hosting_id: h.id,
          stack_user_ref: u.stack_user_ref,
          email: u.contact_email || undefined,
        });
        ok++;
      } catch {
        fail++;
      }
    }
    onToast?.(`Auto-link done: ${ok} ok, ${fail} failed`);
    await load();
  };

  const bulkDeleteSelected = async () => {
    const refs = Object.keys(selected).filter((k) => selected[k]);
    if (!refs.length) return;
    if (!window.confirm(`Delete ${refs.length} stack user(s) on 20i? This cannot be undone.`)) return;
    let ok = 0, fail = 0;
    for (const ref of refs) {
      try {
        const r = await adminStackUserDelete(ref);
        if (r.ok) ok++; else fail++;
      } catch { fail++; }
    }
    onToast?.(`Bulk delete: ${ok} ok, ${fail} failed`);
    setSelected({});
    await load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">20i Stack-user Audit</h2>
          <p className="text-[#11151C]/60 text-sm mt-1">
            Cross-reference users on 20i with Live Mover hosting records. Link orphans, merge manuals, delete stale ones.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="audit-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#11151C]/15 text-sm font-semibold hover:bg-[#FFE6D6]"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Re-scan
          </button>
          <button
            onClick={autoLinkAll}
            disabled={loading || !audit}
            data-testid="audit-autolink"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#FFE6D6] text-[#B23B12] text-sm font-bold hover:brightness-95"
            title="Auto-link unlinked users to hostings whose customer email or domain matches"
          >
            <LinkIcon className="w-4 h-4" /> Auto-link by best match
          </button>
        </div>
      </div>

      {audit && (
        <div className="grid sm:grid-cols-3 gap-3">
          <Section title="Linked" count={audit.linked.length} tone="ok" testid="audit-linked-section">
            {audit.linked.length === 0 ? (
              <p className="text-sm text-[#11151C]/55 px-1 py-2">No linked users.</p>
            ) : (
              <ul className="text-sm divide-y divide-[#11151C]/5 max-h-72 overflow-y-auto">
                {audit.linked.map((u) => (
                  <li key={u.stack_user_ref} className="py-2 flex items-start gap-2">
                    <ShieldCheck className="w-4 h-4 text-green-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{u.name || u.id}</div>
                      <div className="text-xs text-[#11151C]/55 truncate">{u.contact_email || "—"}</div>
                      <div className="text-[11px] text-[#11151C]/45 mt-0.5">
                        {u.local_links.map((l) => `${l.field}=${l.domain}`).join(" · ")}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Unlinked on 20i" count={audit.unlinked.length} tone="warn" testid="audit-unlinked-section">
            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#11151C]/45" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name/email/id"
                  className="w-full pl-7 pr-2 py-1.5 border border-[#11151C]/15 rounded-lg text-xs"
                  data-testid="audit-unlinked-search"
                />
              </div>
            </div>
            {filteredUnlinked.length === 0 ? (
              <p className="text-sm text-[#11151C]/55 px-1 py-2">Nothing unlinked. 🎉</p>
            ) : (
              <ul className="text-sm divide-y divide-[#11151C]/5 max-h-80 overflow-y-auto">
                {filteredUnlinked.map((u) => {
                  const sug = suggestHosting(u);
                  return (
                    <li key={u.stack_user_ref} className="py-2 flex items-start gap-2">
                      <ShieldAlert className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{u.name || u.id}</div>
                        <div className="text-xs text-[#11151C]/55 truncate">{u.contact_email || "no email"}</div>
                        {sug && (
                          <div className="text-[11px] text-[#B23B12] mt-0.5">
                            Suggested → <span className="font-bold">{sug.domain_name}</span>
                          </div>
                        )}
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                          <button
                            disabled={!sug || !!busy[u.stack_user_ref]}
                            onClick={() => linkSupport(u.stack_user_ref, sug.id, u.contact_email)}
                            className="text-[11px] px-2 py-0.5 rounded bg-[#FFE6D6] text-[#B23B12] font-bold disabled:opacity-50"
                            data-testid={`audit-quick-link-${u.id}`}
                          >Link to suggested</button>
                          <button
                            onClick={() => setLinkPick({ ref: u.stack_user_ref, email: u.contact_email })}
                            className="text-[11px] px-2 py-0.5 rounded border border-[#11151C]/15 font-semibold hover:bg-[#FFE6D6]"
                            data-testid={`audit-manual-link-${u.id}`}
                          >Manual link…</button>
                          <button
                            disabled={!!busy[u.stack_user_ref]}
                            onClick={() => deleteOne(u.stack_user_ref)}
                            className="text-[11px] px-2 py-0.5 rounded text-red-600 font-bold hover:bg-red-50 disabled:opacity-50"
                            data-testid={`audit-delete-${u.id}`}
                          >Delete on 20i</button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>

          <Section title="Orphan locally" count={audit.orphan_local.length} tone="bad" testid="audit-orphan-section">
            {audit.orphan_local.length === 0 ? (
              <p className="text-sm text-[#11151C]/55 px-1 py-2">No orphan local references.</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] text-[#11151C]/55">
                    These references point to stack users that no longer exist on 20i. Safe to clear in MongoDB if needed.
                  </p>
                </div>
                <ul className="text-sm divide-y divide-[#11151C]/5 max-h-72 overflow-y-auto">
                  {audit.orphan_local.map((o) => (
                    <li key={o.stack_user_ref} className="py-2">
                      <div className="font-mono text-xs text-[#11151C]/70">{o.stack_user_ref}</div>
                      <div className="text-[11px] text-[#11151C]/55">
                        Referenced by: {o.local_links.map((l) => `${l.domain}(${l.field})`).join(", ")}
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Section>
        </div>
      )}

      {audit && (
        <div className="text-xs text-[#11151C]/50">
          Remote total users on 20i: <b>{audit.total_remote}</b> · Local references: <b>{audit.total_local_links}</b>
        </div>
      )}

      {!audit && !loading && (
        <div className="border border-[#11151C]/10 rounded-2xl p-6 text-center text-[#11151C]/55 text-sm">
          <AlertCircle className="w-5 h-5 mx-auto mb-2" />
          Could not load audit data. Click <b>Re-scan</b> above.
        </div>
      )}

      {/* Manual link picker modal */}
      {linkPick && (
        <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={() => setLinkPick(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="lm-font font-extrabold text-[#11151C] text-lg">Link stack user → hosting</h3>
            <p className="text-sm text-[#11151C]/60 mt-1 break-all"><span className="font-mono">{linkPick.ref}</span></p>
            <p className="text-xs text-[#11151C]/55 mt-2">Pick a hosting to bind this 20i stack user as the Support / Ticket login.</p>
            <div className="mt-3 space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
              {hostings.map((h) => (
                <button
                  key={h.id}
                  onClick={() => linkSupport(linkPick.ref, h.id, linkPick.email)}
                  className="w-full text-left p-2 border border-[#11151C]/10 rounded-lg hover:bg-[#FFFBF6] flex items-center justify-between gap-2"
                  data-testid={`audit-pick-hosting-${h.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{h.domain_name}</div>
                    <div className="text-[11px] text-[#11151C]/55 truncate">{h.customer_name} · {h.customer_email}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#11151C]/40" />
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setLinkPick(null)} className="px-3 py-2 rounded-xl border border-[#11151C]/15 text-sm font-semibold">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
